import { Contract, UnitType } from 'entities/Contract';
import { CachedDiscount } from 'entities/Discount';
import { Floor } from 'entities/Floor';
import { Insurance } from 'entities/Insurance';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { PaymentMethod } from 'entities/Payment';
import { PriceHistoryDocument } from 'entities/PriceHistory';
import { RentOptionDocument } from 'entities/RentOption';
import { SizeCode, SizeCodeType } from 'entities/SizeCode';

export enum LockState {
  OPENED = 'opened',
  CLOSED = 'closed',
  NA = 'N/A',
}

export interface LockStatus {
  lockId: string;
  status: LockState;
}

export interface LockStatusChange {
  lockId: string;
  status: LockState;
  warehouseId: string;
  boxNumber: string;
}

export enum BoxDeactivationReason {
  FOR_OWN_NEEDS = 'for own needs',
  DEBTORS_STUFF = 'debtors stuff',
  HAS_DEFECT = 'has defect',
  NOT_READY_FOR_USE = 'not ready for use',
  RESERVED = 'reserved',
  OTHER = 'other',
}

export enum Subtype {
  CONTAINER = 'container',
  MEZZANINE = 'mezzanine',
  GARAGE = 'garage',
  ROULETTE = 'roulette',
  DOUBLE_DOOR = 'double door',
  EXTERNAL_BOX = 'external box',
  SPECIALITY = 'speciality',
  GROUND_LEVEL = 'ground level',
  COLD_BOX = 'cold box',
}

export interface Box {
  warehouseId: string;
  name: string;
  description: Nullable<string>;
  monthRate: Nullable<number>;
  weekRate: Nullable<number>;
  dailyRate: Nullable<number>;
  price: Nullable<number>;
  priceFactor: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  boxId: string;
  floorId?: string;
  floor?: Nullable<Floor>;
  priceHistory?: PriceHistoryDocument[];
  width: number;
  height: number;
  length: number;
  doorWidth: number;
  sizeCode: SizeCode;
  isSelectedOnBooking: boolean;
  note: Nullable<string>;
  square: number;
  lockAddress?: string;
  maxOpeningTimeMS?: Nullable<number>;
  timeToOpenLockMS?: Nullable<number>;
  contractNumber: Nullable<string>;
  reservationNumber: Nullable<string>;
  deactivationReason?: Nullable<BoxDeactivationReason>;
  unitType: UnitType;
  imageUrl?: string;
  sizeCodeId: string;
  rentOptions?: Nullable<RentOptionDocument[]>;
  specialityDescription?: Nullable<string>;
  subtype?: Nullable<Subtype>;
}

export interface BoxForListing {
  unitType: UnitType;
  name: string;
  boxId: string;
  warehouseId: string;
  warehouseName: string;
  sizeCodeId: string;
  square: number;
  width: number;
  height?: number;
  length: number;
  lockAddress?: string;
  monthRate?: number;
  weekRate?: number;
  dailyRate?: number;
  priceFactor: number;
  sizeCodeType: SizeCodeType;
  note?: string;
  description?: string;
  isActive: boolean;
  chargedTo?: string;
  paidTo?: string;
  contractId?: string;
  contractNumber?: string;
  reservationId?: string;
  reservationNumber?: number;
  expirationDate?: string;
  floorId?: string;
  floorName?: string;
  userId?: string;
  userEmail?: string;
  userFirstName?: string;
  userLastName?: string;
  lockStatus: LockState;
  lastOpened: string;
  isContractPaid?: boolean;
  maxOpeningTimeMS?: number;
  timeToOpenLockMS?: Nullable<number>;
  deactivationReason?: Nullable<BoxDeactivationReason>;
}

export interface ActivateBoxParams {
  boxId: string;
  shouldCreateWaitListNote: boolean;
}

export interface CachedBox extends Box {
  insurance: Nullable<Insurance>;
  floor?: Nullable<Floor>;
  originalEntityId: string;
  square: number;
  discounts: CachedDiscount[];
  contract: Contract;
}

export interface BookAndPayData {
  paymentMethod: PaymentMethod;
  isAutopayment: boolean;
  boxId: string;
  userId: string;
  warehouseId: string;
  insuranceId?: Nullable<string>;
  rentOptionId: string;
  startDate: string;
  endDate: string;
  invoiceFrequencyType: InvoiceFrequencyType;
  invoiceFrequency: number;
  paymentFrequencyPeriod: string;
  useUserBalance: boolean;
  promoCode?: Nullable<string>;
}

export interface CreateBoxParams {
  warehouseId: string;
  name: string;
  sizeCodeId: string;
  length: number;
  width: number;
  height?: number;
  dailyRate?: number;
  weekRate?: number;
  monthRate?: number;
  description?: string;
  isFowOwnNeeds?: boolean;
  maxOpeningTimeMS?: Nullable<number>;
  timeToOpenLockMS?: Nullable<number>;
  lockAddress?: string;
  floorId?: string;
  unitType: UnitType;
  subtype?: Nullable<Subtype>;
  specialityDescription?: string;
}

export interface LockActionData {
  lockAddress: string;
  warehouse: string;
  boxNumber: string;
}

export interface UpdateBoxesParams extends Partial<CreateBoxParams> {
  id: string;
}
